import { Option } from "@/components/select/AsyncAutocompleteSelect";
import { toaster } from "@/components/ui/toaster";
import { RequestTypes } from "@/constants/enums";
import { AppRoutes } from "@/constants/routes";
import { createFertSlotRequest } from "@/services/fert-slot-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { parseTonInputToNumber } from "@/utils/query-string";
import dayjs from "dayjs";
import { NavigateFunction } from "react-router-dom";

interface CreateFertSlotRequestHandlerProps {
  values: any;
  setLoadingCreate: (loading: boolean) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  procedenciaItems: Option[];
}

export const createFertSlotRequestHandler = async ({
  values,
  setLoadingCreate,
  navigate,
  fetchPermissions,
  procedenciaItems,
}: CreateFertSlotRequestHandlerProps) => {
  const normalizeSingle = (v: any) => (Array.isArray(v) ? v?.[0] : v);

  const selectedCuenta = normalizeSingle(values?.account?.value);
  const cuenta = Number(selectedCuenta);
  if (!Number.isFinite(cuenta) || cuenta <= 0) return;

  const idDeposito = Number(normalizeSingle(values?.idDeposito));
  if (!Number.isFinite(idDeposito) || idDeposito <= 0) return;

  const destinoRaw = String(values?.destino ?? "").trim();
  const matched = procedenciaItems.find((p) => String(p.value) === destinoRaw);
  const destino = matched?.label ?? destinoRaw;
  const destinoAlt = matched ? "" : destinoRaw;

  const kmDistancia = parseTonInputToNumber(values?.kmDistancia) ?? 0;
  const contacto = String(values?.contacto ?? "").trim();
  const nombreContacto = String(values?.nombreContacto ?? "").trim();
  const observaciones = String(values?.observaciones ?? "").trim();

  const productos = (values?.productos ?? []).map((p: any) => ({
    idProducto: Number(normalizeSingle(p?.idProducto)),
    cantidad: parseTonInputToNumber(p?.cantidad) ?? 0,
  }));

  setLoadingCreate(true);
  try {
    await createFertSlotRequest({
      cuenta,
      productos,
      fechaEntrega: dayjs(values?.fechaEntrega).toISOString(),
      destino,
      ...(destinoAlt ? { destinoAlt } : {}),
      kmDistancia,
      contacto,
      ...(nombreContacto ? { nombreContacto } : {}),
      ...(observaciones ? { observaciones } : { observaciones: "" }),
      tipoSolicitud: RequestTypes.CuposFertilizante,
      idDeposito,
    });

    toaster.create({
      type: "success",
      title: "Solicitud creada exitosamente.",
    });

    navigate(AppRoutes.FERT_SLOTS_REQ);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FERT_SLOTS_REQ,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo crear la solicitud."),
      });
    }

    throw error;
  } finally {
    setLoadingCreate(false);
  }
};
